// Odd identity, field evidence and bounded recovery projection are owned by
// the pure projection helper; list/detail public exports remain compatible.
import {
  fxId,
  mergeOddPreservingIdentity,
  sportsnewDisplayOddGroupEvidence,
  withSportsnewDisplayOddEvidence,
  mapSportsnewMarketOdds,
  mapSportsnewFixtureOdds,
  sportsnewListPatchFixtures,
  applySportsnewOddPatchesToMarkets,
  oddPatchId,
  oddItemId,
  normalizeOddPatchForList,
  fixtureArrayFromPatchFixtures,
} from './sportsnewOddProjection.js';
export {
  fxId,
  normalizeAcceptedSportsnewOddAvailability,
  mergeOddPreservingIdentity,
  SPORTSNEW_ODD_GROUP_FIELDS,
  sportsnewOddEvidence,
  sportsnewDisplayOddGroupEvidence,
  withSportsnewDisplayOddEvidence,
  compareSportsnewOddEvidence,
  mapSportsnewMarketOdds,
  mapSportsnewFixtureOdds,
  sportsnewListPatchFixtures,
  mapSportsnewListPatchOdds,
  trackSportsnewListRecoveryFields,
  filterSportsnewListRecoveryFields,
  reconcileSportsnewListRecoveryRows,
  applySportsnewOddPatchesToMarkets,
  normalizeSportsnewListRecovery,
  oddStateIdKey,
  normalizeOddPatchForList,
} from './sportsnewOddProjection.js';

/* ── Merge one mainOdds slot (home/draw/away), never letting a leaner
   incoming tick blow away stable identity fields (canonicalSelectionKey/
   canonicalLineKey/canonicalPairKey/selectionLine/groupingKey/shortOddId)
   that only the initial REST fetch carried. A price/state tick only needs to
   overlay the fields it actually sends. ── */
const mergeMainOddsSlot = (oldSlot, newSlot) => {
  if (!newSlot) return oldSlot;
  if (!oldSlot) return newSlot;
  const oldId = oddItemId(oldSlot), newId = oddItemId(newSlot);
  // A replacement selection owns its identity, availability and price.
  if (oldId != null && newId != null && oldId !== newId) return newSlot;
  return mergeOddPreservingIdentity(oldSlot, newSlot);
};

/* ── Merge a fixture's mainOdds object slot-by-slot (see mergeMainOddsSlot)
   instead of replacing it wholesale — a live tick routinely carries only
   {oddStateId, priceDecimal, bettable, state} per slot, and a plain object
   spread at the fixture level would otherwise silently drop every other
   identity field the bet-placement payload needs. ── */
const mergeMainOdds = (oldMainOdds, newMainOdds) => {
  if (!newMainOdds) return oldMainOdds;
  if (!oldMainOdds) return newMainOdds;
  return {
    home: mergeMainOddsSlot(oldMainOdds.home, newMainOdds.home),
    draw: mergeMainOddsSlot(oldMainOdds.draw, newMainOdds.draw),
    away: mergeMainOddsSlot(oldMainOdds.away, newMainOdds.away),
  };
};

/* ── Merge an existing fixture with an incoming (possibly partial) fixture
   patch, keeping mainOdds identity fields stable across leaner ticks. ── */
const mergeFixture = (oldFixture, newFixture) => {
  const merged = { ...oldFixture, ...newFixture };
  // Incoming array membership/line structure remains authoritative. Only
  // selections that still exist inherit omitted (or stale-filtered) fields.
  const oldById = new Map();
  mapSportsnewFixtureOdds(oldFixture, odd => {
    const id = oddItemId(odd);
    if (id != null) oldById.set(id, mergeOddPreservingIdentity(oldById.get(id), odd));
    return odd;
  });
  for (const key of ['marketsPreview', 'markets', 'marketSnapshot']) {
    if (Array.isArray(newFixture[key])) merged[key] = mapSportsnewMarketOdds(newFixture[key], odd => {
      const old = oldById.get(oddItemId(odd));
      return old ? mergeOddPreservingIdentity(old, odd) : odd;
    });
  }
  if (newFixture.mainOdds) merged.mainOdds = mergeMainOdds(oldFixture.mainOdds, newFixture.mainOdds);
  const updates = [];
  mapSportsnewFixtureOdds(newFixture, odd => { updates.push(odd); return odd; });
  for (const key of ['marketsPreview', 'markets']) {
    if (Array.isArray(merged[key])) merged[key] = applySportsnewOddPatchesToMarkets(merged[key], updates);
  }
  if (merged.mainOdds && updates.length) {
    const byId = new Map(updates.map(odd => [oddItemId(odd), odd]));
    const mainOdds = { ...merged.mainOdds };
    for (const slot of ['home', 'draw', 'away']) {
      const update = byId.get(oddItemId(mainOdds[slot]));
      if (update) mainOdds[slot] = mergeOddPreservingIdentity(mainOdds[slot], normalizeOddPatchForList(update));
    }
    merged.mainOdds = mainOdds;
  }
  return merged;
};

const hasJsonPatchOps = (ops) => Array.isArray(ops) && ops.some((op) => typeof op?.path === 'string');

/* ── Helper: apply a JSON Patch array (RFC 6902 subset) to an object ── */
const applyJsonPatchToObject = (target, ops = []) => {
  if (!Array.isArray(ops) || ops.length === 0) return target;
  let obj = { ...target };
  for (const op of ops) {
    const pathParts = (op.path || '').replace(/^\//, '').split('/');
    if (op.op === 'replace' || op.op === 'add') {
      let cur = obj;
      for (let i = 0; i < pathParts.length - 1; i++) {
        cur[pathParts[i]] = Array.isArray(cur[pathParts[i]])
          ? [...cur[pathParts[i]]]
          : { ...cur[pathParts[i]] };
        cur = cur[pathParts[i]];
      }
      cur[pathParts[pathParts.length - 1]] = op.value;
    } else if (op.op === 'remove') {
      let cur = obj;
      for (let i = 0; i < pathParts.length - 1; i++) {
        cur = cur[pathParts[i]];
        if (!cur) break;
      }
      if (cur) delete cur[pathParts[pathParts.length - 1]];
    }
  }
  return obj;
};

/* ── Apply a fixture-detail patch to a single fixture. Kept in this pure
   helper module so detail state and list state use the same JSON patch logic. ── */
export const applyFixturePatchToFixture = (fixture, patch) => {
  if (!fixture || !patch) return fixture;
  if (Array.isArray(patch.ops)) {
    return hasJsonPatchOps(patch.ops) ? applyJsonPatchToObject(fixture, patch.ops) : fixture;
  }
  return { ...fixture, ...patch };
};

const mergeMainOddsWithOddPatch = (mainOdds, patch) => {
  if (!mainOdds) return { mainOdds, changed: false };
  const targetId = oddPatchId(patch);
  if (targetId == null) return { mainOdds, changed: false };

  let changed = false;
  const nextMainOdds = { ...mainOdds };
  ['home', 'draw', 'away'].forEach((slot) => {
    const oldSlot = mainOdds[slot];
    if (oddItemId(oldSlot) !== targetId) return;
    nextMainOdds[slot] = mergeOddPreservingIdentity(oldSlot, normalizeOddPatchForList(patch));
    changed = true;
  });

  return { mainOdds: changed ? nextMainOdds : mainOdds, changed };
};

const mergeMarketsWithOddPatch = (markets, patch) => {
  if (!Array.isArray(markets)) return { markets, changed: false };
  const targetId = oddPatchId(patch);
  if (targetId == null) return { markets, changed: false };

  let anyChanged = false;
  const nextMarkets = markets.map((market) => {
    const oddsKey = market?.selections ? 'selections' : market?.odds ? 'odds' : null;
    if (!oddsKey || !Array.isArray(market[oddsKey])) return market;

    let marketChanged = false;
    const nextOdds = market[oddsKey].map((odd) => {
      if (oddItemId(odd) !== targetId) return odd;
      marketChanged = true;
      return mergeOddPreservingIdentity(odd, normalizeOddPatchForList(patch));
    });

    if (!marketChanged) return market;
    anyChanged = true;
    return { ...market, [oddsKey]: nextOdds };
  });

  return { markets: anyChanged ? nextMarkets : markets, changed: anyChanged };
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const boolOrNull = (value) => {
  if (value === true || value === false) return value;
  return null;
};

export const marketStatusPatchId = (market) => {
  const id = market?.marketStateId ?? market?.market_state_id ?? market?.id ?? market?.market_id;
  return id != null ? String(id) : null;
};

const oddMarketStatusId = (odd) => {
  const id = odd?.marketStateId ?? odd?.market_state_id;
  return id != null ? String(id) : null;
};

const marketStatusTypeKey = (market) => {
  const key = market?.marketTypeKey ?? market?.market_type_key ?? market?.marketId ?? market?.market_id;
  return key != null ? String(key) : null;
};

const oddMarketTypeKey = (odd) => {
  const key = odd?.marketTypeKey ?? odd?.market_type_key ?? odd?.marketId ?? odd?.market_id;
  return key != null ? String(key) : null;
};

export const marketStatusMapFrom = (markets) => {
  const map = new Map();
  (markets || []).forEach((market) => {
    const id = marketStatusPatchId(market);
    if (id != null && !map.has(id)) map.set(id, market);
  });
  return map;
};

const marketStatusBoolean = (market, keys) => {
  for (const key of keys) {
    const value = boolOrNull(market?.[key]);
    if (value !== null) return value;
    const stateValue = boolOrNull(market?.marketState?.[key]);
    if (stateValue !== null) return stateValue;
  }
  return null;
};

const marketStatusText = (market) => firstDefined(
  market?.marketState?.state,
  market?.state,
  market?.marketState?.displayState,
  market?.displayState,
  market?.marketState?.reason,
  market?.reason,
  market?.marketState?.lockReason,
  market?.lockReason,
);

const marketStatusReason = (market) => firstDefined(
  market?.marketState?.reason,
  market?.reason,
  market?.marketState?.lockReason,
  market?.lockReason,
);

const isMarketStatusLocked = (market) => {
  if (!market) return false;
  if (marketStatusBoolean(market, ['bettable', 'isBettable', 'is_bettable']) === false) return true;
  if (marketStatusBoolean(market, ['suspended', 'isSuspended', 'is_suspended']) === true) return true;
  const status = marketStatusText(market);
  return typeof status === 'string' && /lock|suspend|closed|unavailable|hidden/i.test(status);
};

const childLockPatchForMarketStatus = (market) => {
  const status = marketStatusText(market);
  const state = typeof status === 'string' && status.toUpperCase() !== 'OPEN' ? status : 'LOCKED';
  const reason = marketStatusReason(market) ?? 'MARKET_LOCKED';
  return {
    bettable: false,
    isBettable: false,
    suspended: true,
    is_suspended: true,
    locked: true,
    state,
    displayState: state,
    reason,
    lockReason: reason,
  };
};

const mergeOddWithMarketStatusLock = (odd, marketPatch) => {
  if (!isMarketStatusLocked(marketPatch)) return odd;
  return withSportsnewDisplayOddEvidence({ ...odd, ...childLockPatchForMarketStatus(marketPatch) }, sportsnewDisplayOddGroupEvidence(odd));
};

const oddMatchesMarketStatusPatch = (odd, marketPatch) => {
  const patchId = marketStatusPatchId(marketPatch);
  const oddMarketId = oddMarketStatusId(odd);
  if (patchId != null && oddMarketId != null) return patchId === oddMarketId;

  const patchType = marketStatusTypeKey(marketPatch);
  const oddType = oddMarketTypeKey(odd);
  return patchType != null && oddType != null && patchType === oddType;
};

const mergeMainOddsWithMarketStatusPatch = (mainOdds, marketPatch) => {
  if (!mainOdds || !isMarketStatusLocked(marketPatch)) return { mainOdds, changed: false };

  let changed = false;
  const nextMainOdds = { ...mainOdds };
  ['home', 'draw', 'away'].forEach((slot) => {
    const oldSlot = mainOdds[slot];
    if (!oldSlot || !oddMatchesMarketStatusPatch(oldSlot, marketPatch)) return;
    nextMainOdds[slot] = mergeOddWithMarketStatusLock(oldSlot, marketPatch);
    changed = true;
  });

  return { mainOdds: changed ? nextMainOdds : mainOdds, changed };
};

export const mergeMarketWithStatusPatch = (market, marketPatch, flashEvents = null) => {
  const nextBettable = marketStatusBoolean(marketPatch, ['bettable', 'isBettable', 'is_bettable']);
  const nextSuspended = marketStatusBoolean(marketPatch, ['suspended', 'isSuspended', 'is_suspended']);
  const key = market?.selections ? 'selections' : market?.odds ? 'odds' : null;

  let changed = false;
  let nextMarket = {
    ...market,
    ...(nextBettable !== null && { bettable: nextBettable, isBettable: nextBettable }),
    ...(nextSuspended !== null && { suspended: nextSuspended, is_suspended: nextSuspended }),
    ...(marketPatch.marketState !== undefined && { marketState: marketPatch.marketState }),
  };

  changed = nextMarket !== market;

  if (key && Array.isArray(market[key]) && isMarketStatusLocked(marketPatch)) {
    const nextItems = market[key].map((odd) => {
      const lockedOdd = mergeOddWithMarketStatusLock(odd, marketPatch);
      if (flashEvents && odd.oddStateId != null) {
        const direction = resolveOddFlash(odd, lockedOdd);
        if (direction) flashEvents.push({ oddStateId: odd.oddStateId, direction });
      }
      if (lockedOdd !== odd) changed = true;
      return lockedOdd;
    });
    nextMarket = { ...nextMarket, [key]: nextItems };
  }

  return { market: changed ? nextMarket : market, changed };
};

const mergeMarketsWithMarketStatusPatch = (markets, marketPatch, flashEvents = null) => {
  if (!Array.isArray(markets)) return { markets, changed: false };
  const targetId = marketStatusPatchId(marketPatch);
  if (targetId == null) return { markets, changed: false };

  let anyChanged = false;
  const nextMarkets = markets.map((market) => {
    if (marketStatusPatchId(market) !== targetId) return market;
    const result = mergeMarketWithStatusPatch(market, marketPatch, flashEvents);
    if (result.changed) anyChanged = true;
    return result.market;
  });

  return { markets: anyChanged ? nextMarkets : markets, changed: anyChanged };
};

const statusOnlyMarketPatchesFromFixturePatch = (patch) => {
  if (!Array.isArray(patch?.markets)) return [];
  return patch.markets.filter((market) => (market?.selections ?? market?.odds ?? []).length === 0);
};

const applyMarketStatusPatchToFixture = (fixture, marketPatch) => {
  const mainResult = mergeMainOddsWithMarketStatusPatch(fixture.mainOdds, marketPatch);
  const marketsResult = mergeMarketsWithMarketStatusPatch(fixture.markets, marketPatch);

  if (!mainResult.changed && !marketsResult.changed) return fixture;
  return {
    ...fixture,
    ...(mainResult.changed && { mainOdds: mainResult.mainOdds }),
    ...(marketsResult.changed && { markets: marketsResult.markets }),
  };
};

/* ── Apply a selected/odd patch to the list state. selected:patch updates can
   be the only event a visible list cell receives; oddsFlash is keyed globally
   by oddStateId, but the list card renders from fixture.mainOdds (and some
   domestic rows from fixture.markets). Keep both copies in sync so cells do
   not flash while still showing stale price/lock state until refresh. ── */
export const applyOddPatchToFixtureList = (fixtures, patch) => {
  if (!Array.isArray(fixtures) || oddPatchId(patch) == null) return fixtures;

  let anyChanged = false;
  const nextFixtures = fixtures.map((fixture) => {
    const mainResult = mergeMainOddsWithOddPatch(fixture.mainOdds, patch);
    const marketsResult = mergeMarketsWithOddPatch(fixture.markets, patch);
    const previewResult = mergeMarketsWithOddPatch(fixture.marketsPreview, patch);

    if (!mainResult.changed && !marketsResult.changed && !previewResult.changed) return fixture;
    anyChanged = true;
    return {
      ...fixture,
      ...(mainResult.changed && { mainOdds: mainResult.mainOdds }),
      ...(marketsResult.changed && { markets: marketsResult.markets }),
      ...(previewResult.changed && { marketsPreview: previewResult.markets }),
    };
  });

  return anyChanged ? nextFixtures : fixtures;
};

const fixturePatchId = (patch, fallbackFixtureId = null) => {
  const id =
    patch?.fixtureId ??
    patch?.fixture_id ??
    patch?.id ??
    patch?.fixture?.fixtureId ??
    patch?.fixture?.fixture_id ??
    patch?.fixture?.id ??
    fallbackFixtureId;
  return id != null ? String(id) : null;
};

const FIXTURE_LIST_PATCH_FIELDS = [
  'fixtureId',
  'fixture_id',
  'id',
  'sport',
  'sportSlug',
  'sportKey',
  'leagueId',
  'league_id',
  'leagueName',
  'leagueNameKo',
  'league',
  'home',
  'away',
  'homeLogo',
  'awayLogo',
  'startsAt',
  'startTime',
  'starts_at',
  'status',
  'period',
  'periodNumber',
  'clock',
  'score',
  'live_score_home',
  'live_score_away',
  'score_home',
  'score_away',
  'scoreHome',
  'scoreAway',
  'marketCount',
  'marketState',
  'mainOdds',
  'mainOddsStatus',
  'updatedAt',
  'stale',
  'recovery',
  'rowOrder',
  'row_order',
  'esports',
];

const fixtureListFieldPatch = (patch) => {
  if (patch?.fixture) return patch.fixture;
  const fieldPatch = {};
  FIXTURE_LIST_PATCH_FIELDS.forEach((key) => {
    if (patch?.[key] !== undefined) fieldPatch[key] = patch[key];
  });
  return Object.keys(fieldPatch).length > 0 ? fieldPatch : null;
};

const oddPatchesFromFixturePatch = (patch) => {
  const odds = [];
  if (Array.isArray(patch?.markets)) {
    patch.markets.forEach((market) => {
      const items = market?.selections ?? market?.odds ?? [];
      if (Array.isArray(items)) odds.push(...items);
    });
  }
  if (Array.isArray(patch?.odds)) odds.push(...patch.odds);
  if (patch?.oddStateId != null) odds.push(patch);
  return odds;
};

/* ── Apply a fixture-room patch to the list state as well. The open detail
   room can be the only stream carrying a price/lock/status change for the
   visible fixture; oddsFlash is global, so the list cell may blink even while
   fixture.mainOdds remains stale unless this patch also updates fixtures[]. ── */
export const applyFixturePatchToFixtureList = (fixtures, patch, fallbackFixtureId = null) => {
  if (!Array.isArray(fixtures) || !patch) return fixtures;
  const targetId = fixturePatchId(patch, fallbackFixtureId);
  let nextFixtures = fixtures;

  if (targetId != null) {
    let metadataChanged = false;
    const fieldPatch = fixtureListFieldPatch(patch);
    nextFixtures = nextFixtures.map((fixture) => {
      if (fxId(fixture) !== targetId) return fixture;

      let nextFixture = fixture;
      if (hasJsonPatchOps(patch.ops)) nextFixture = applyJsonPatchToObject(nextFixture, patch.ops);
      if (fieldPatch) nextFixture = mergeFixture(nextFixture, fieldPatch);

      if (nextFixture !== fixture) metadataChanged = true;
      return nextFixture;
    });
    if (!metadataChanged) nextFixtures = fixtures;
  }

  oddPatchesFromFixturePatch(patch).forEach((oddPatch) => {
    nextFixtures = applyOddPatchToFixtureList(nextFixtures, oddPatch);
  });

  statusOnlyMarketPatchesFromFixturePatch(patch).forEach((marketPatch) => {
    nextFixtures = nextFixtures.map((fixture) => {
      if (targetId != null && fxId(fixture) !== targetId) return fixture;
      return applyMarketStatusPatchToFixture(fixture, marketPatch);
    });
  });

  return nextFixtures;
};

/* ── Apply a list patch to a fixtures array ── */
export const applyListPatch = (fixtures, patch) => {
  if (!patch) return fixtures;

  // "Full replace" shape — but confirmed via debug logging that this event does NOT
  // reliably carry a full room snapshot: it is routinely a single recently-changed
  // fixture reusing the same `{ fixtures: [...] }` envelope (observed: every tick
  // carries exactly 1 fixture, rotating between unrelated matches of different
  // sports). Treating it as authoritative and pruning anything absent from it wiped
  // out every other live fixture on nearly every tick. Always upsert-merge instead —
  // never prune here. Removal only happens via an explicit ops:remove (below) or a
  // fresh REST fetch (sport/league/page change).
  //
  // UPDATE existing fixtures only — never INSERT a brand-new fixtureId here.
  // The WS list room this subscribes to is broader than "just this page"
  // (e.g. every league under a mode+sport), so treating an unseen fixtureId
  // as an insert made a fixed-size page (e.g. 30 items) silently grow without
  // bound as unrelated matches elsewhere in the room ticked in — this is what
  // looked like the match list "infinitely loading" while scrolling, with no
  // REST call involved at all. A genuinely new match should only become
  // visible via an explicit refresh, page change, or filter change.
  const patchFixtures = fixtureArrayFromPatchFixtures(patch.fixtures);
  if (patchFixtures.length > 0) {
    const inMap = new Map();
    for (const f of patchFixtures) {
      const id = fxId(f);
      if (id != null && !inMap.has(id)) inMap.set(id, f);
    }
    const incoming = Array.from(inMap.values());

    const map = new Map(fixtures.map((f) => [fxId(f), f]));
    incoming.forEach((f) => {
      const id = fxId(f);
      if (!map.has(id)) return;
      map.set(id, mergeFixture(map.get(id), f));
    });
    return Array.from(map.values());
  }

  // Incremental ops: upsert / remove individual fixture cards.
  // Backend sends the op kind as `op.op`, not `op.type` — accept both so either
  // contract works instead of silently dropping every op.
  if (Array.isArray(patch.ops)) {
    const map = new Map(fixtures.map((f) => [fxId(f), f]));
    for (const op of patch.ops) {
      const kind = op.op ?? op.type;
      if (kind === 'upsert' && op.fixture) {
        // Same reasoning as the patch.fixtures branch above — update only,
        // never insert a fixture this page hasn't already shown.
        const id = fxId(op.fixture);
        if (id != null && map.has(id)) map.set(id, mergeFixture(map.get(id), op.fixture));
      } else if (kind === 'remove') {
        const id = fxId(op);
        if (id != null) map.delete(id);
      }
    }
    return Array.from(map.values());
  }

  // Odds / status field patches on individual fixtures.
  // Backend sends `fields` as an array of changed field NAMES (e.g. ['mainOdds']),
  // with the actual updated values as sibling top-level keys on `patch` itself
  // (e.g. patch.mainOdds) — not nested inside `patch.fields`. Still accept the
  // older object-shaped `fields` (name → value) as a fallback.
  if (patch.fixtureId != null && patch.fields) {
    const fieldNames = Array.isArray(patch.fields) ? patch.fields : Object.keys(patch.fields);
    const fieldData = {};
    fieldNames.forEach((name) => {
      if (patch[name] !== undefined) fieldData[name] = patch[name];
      else if (!Array.isArray(patch.fields) && patch.fields[name] !== undefined) fieldData[name] = patch.fields[name];
    });
    return fixtures.map((f) => {
      if (fxId(f) !== String(patch.fixtureId)) return f;
      return mergeFixture(f, fieldData);
    });
  }

  return fixtures;
};

const MEMBERSHIP_REFRESH_PENDING_LOCK = Object.freeze({
  bettable: false,
  isBettable: false,
  is_bettable: false,
  suspended: true,
  isSuspended: true,
  is_suspended: true,
  locked: true,
  isLocked: true,
  state: 'LOCKED',
  displayState: 'LOCKED',
  reason: 'MEMBERSHIP_REFRESH_PENDING',
  lockReason: 'MEMBERSHIP_REFRESH_PENDING',
});

const failCloseSportsnewOdd = odd => (
  odd && typeof odd === 'object'
    ? withSportsnewDisplayOddEvidence({ ...odd, ...MEMBERSHIP_REFRESH_PENDING_LOCK }, sportsnewDisplayOddGroupEvidence(odd))
    : odd
);

const failCloseSportsnewMainOdds = (mainOdds) => {
  if (Array.isArray(mainOdds)) return mainOdds.map(failCloseSportsnewOdd);
  if (!mainOdds || typeof mainOdds !== 'object') return mainOdds;
  return Object.fromEntries(Object.entries(mainOdds).map(([key, value]) => [
    key,
    value && typeof value === 'object' ? failCloseSportsnewOdd(value) : value,
  ]));
};

const failCloseSportsnewMarket = (market) => {
  if (!market || typeof market !== 'object') return market;
  const oddsKey = Array.isArray(market.selections)
    ? 'selections'
    : Array.isArray(market.odds) ? 'odds' : null;
  return {
    ...market,
    ...MEMBERSHIP_REFRESH_PENDING_LOCK,
    marketState: {
      ...(market.marketState && typeof market.marketState === 'object' ? market.marketState : {}),
      ...MEMBERSHIP_REFRESH_PENDING_LOCK,
    },
    ...(oddsKey ? { [oddsKey]: market[oddsKey].map(failCloseSportsnewOdd) } : {}),
  };
};

/**
 * Keep membership/card identity stable until the authoritative REST list and
 * aggregate counts arrive, but never leave a terminal/removal candidate
 * bettable during that bounded refresh/recovery window.
 */
export const failCloseSportsnewFixtureOdds = (fixture) => {
  if (!fixture || typeof fixture !== 'object') return fixture;
  return {
    ...fixture,
    ...MEMBERSHIP_REFRESH_PENDING_LOCK,
    marketState: {
      ...(fixture.marketState && typeof fixture.marketState === 'object' ? fixture.marketState : {}),
      ...MEMBERSHIP_REFRESH_PENDING_LOCK,
    },
    ...(fixture.mainOdds ? { mainOdds: failCloseSportsnewMainOdds(fixture.mainOdds) } : {}),
    ...(Array.isArray(fixture.markets)
      ? { markets: fixture.markets.map(failCloseSportsnewMarket) }
      : {}),
  };
};

export const failCloseSportsnewFixtureListOdds = (fixtures, fixtureIds = []) => {
  if (!Array.isArray(fixtures)) return fixtures;
  const targets = new Set((Array.isArray(fixtureIds) ? fixtureIds : [fixtureIds])
    .filter(value => value != null)
    .map(String));
  if (targets.size === 0) return fixtures;
  let changed = false;
  const nextFixtures = fixtures.map((fixture) => {
    if (!targets.has(fxId(fixture))) return fixture;
    changed = true;
    return failCloseSportsnewFixtureOdds(fixture);
  });
  return changed ? nextFixtures : fixtures;
};

export const failCloseSportsnewDetailMarkets = markets => (
  Array.isArray(markets) ? markets.map(failCloseSportsnewMarket) : markets
);

/* ── Whether an odd is currently locked/unbettable, regardless of which of
   the several field names a given payload shape uses for it (all confirmed
   present across real captured list/detail/selected patches). ── */
export const isOddLocked = (odd) => {
  if (!odd) return false;
  if (odd.bettable === false) return true;
  if (odd.isBettable === false) return true;
  if (odd.suspended === true) return true;
  if (odd.is_suspended === true) return true;
  if (odd.locked === true) return true;
  if (typeof odd.state === 'string' && /suspend|lock|closed|inactive/i.test(odd.state)) return true;
  if (typeof odd.displayState === 'string' && /suspend|lock|closed|inactive/i.test(odd.displayState)) return true;
  return false;
};

/* ── Resolve the flash direction between an odd's old and new state: a price
   move takes priority (up/down); otherwise a bettable/suspended/locked/
   displayState transition still flashes ('locked' on lock, 'unlocked' on
   reopen) so a lock/suspend is never silently invisible just because the
   price didn't move. Returns null when neither price nor lock-state changed. ── */
export const resolveOddFlash = (oldOdd, newOdd) => {
  if (!oldOdd || !newOdd) return null;
  const oldPrice = oldOdd.priceDecimal != null ? Number(oldOdd.priceDecimal) : null;
  const newPrice = newOdd.priceDecimal != null ? Number(newOdd.priceDecimal) : null;
  if (Number.isFinite(oldPrice) && Number.isFinite(newPrice)) {
    const oldDisplayPrice = oldPrice.toFixed(2);
    const newDisplayPrice = newPrice.toFixed(2);
    if (oldDisplayPrice !== newDisplayPrice) {
      return Number(newDisplayPrice) > Number(oldDisplayPrice) ? 'up' : 'down';
    }
  }
  const wasLocked = isOddLocked(oldOdd);
  const nowLocked = isOddLocked(newOdd);
  if (wasLocked !== nowLocked) return nowLocked ? 'locked' : 'unlocked';
  return null;
};

/* ── Compare a fixture's old/new mainOdds (home/draw/away) and return the
   slots whose price OR lock/unlock state actually changed, with the flash
   direction ── */
export const diffMainOdds = (oldMainOdds, newMainOdds) => {
  const changes = [];
  if (!oldMainOdds || !newMainOdds) return changes;
  ['home', 'draw', 'away'].forEach((slot) => {
    const oldSlot = oldMainOdds[slot];
    const newSlot = newMainOdds[slot];
    if (!oldSlot?.oddStateId || !newSlot) return;
    const direction = resolveOddFlash(oldSlot, newSlot);
    if (direction) changes.push({ oddStateId: oldSlot.oddStateId, direction });
  });
  return changes;
};

/* ── Given a raw sportsnew:list:patch payload and the currently-known
   fixtures, resolve the { oldMainOdds, newMainOdds } pairs that need
   diffing for a flash. Confirmed via a captured real payload: the updated
   mainOdds lives inside each item of `patch.fixtures[]` — a single-item
   array per changed fixture, always a COMPLETE fixture object — NOT on a
   top-level `patch.mainOdds` sibling, and NOT inside `patch.ops[].fixture`
   (the real ops there are lightweight markers — {op, fixtureId, mode, sport}
   — with no fixture data at all, even when `patch.fixtures` is present in
   the very same message). `patch.fixtures` is the authoritative source; the
   other two shapes are kept only as defensive fallbacks for any backend
   variant that might send odds updates differently. ── */
export const resolveMainOddsUpdatesFromListPatch = (patch, oldFixtures) => {
  const oldById = new Map((oldFixtures ?? []).map(fixture => [fxId(fixture), fixture]));
  return sportsnewListPatchFixtures(patch).flatMap(fixture => {
    const old = oldById.get(fxId(fixture));
    return old?.mainOdds && fixture.mainOdds
      ? [{ oldMainOdds: old.mainOdds, newMainOdds: mergeMainOdds(old.mainOdds, fixture.mainOdds) }]
      : [];
  });
};

/* ── If this list-patch's fixture is the one currently open in the detail
   panel, push its fresh mainOdds prices into the detail panel's own markets
   array. Confirmed via captured live traffic: sportsnew:fixture:patch /
   selected:patch do not reliably fire for a plain moneyline tick — mainOdds
   updates flow almost exclusively through this list-patch stream, even for
   whichever fixture's detail panel happens to be open. Without this, the
   "메인"/머니라인 row in the detail panel never refreshes even though the list
   price and its flash both do. Returns the same `detailMarkets` reference
   unchanged when there's nothing to update. ── */
export const applyMainOddsToDetailMarkets = (patch, openFixtureId, detailMarkets) => {
  if (openFixtureId == null) return detailMarkets;
  const odds = [];
  sportsnewListPatchFixtures(patch).forEach(fixture => {
    if (fxId(fixture) !== String(openFixtureId)) return;
    mapSportsnewFixtureOdds(fixture, odd => { odds.push(odd); return odd; });
  });
  return applySportsnewOddPatchesToMarkets(detailMarkets, odds);
};

// Canonical rows own membership/visibility. Replayed public hot values are
// display-only partial patches; never replay an old remove/reopen over that
// newer structural snapshot or call the event/financial observation owners.
export const applySportsnewPresentationReplayRows = (rows, deltas = []) => {
  let next = rows;
  for (const delta of deltas) {
    const fixtures = sportsnewListPatchFixtures(delta.patch).map(fixture => ({
      fixtureId: fxId(fixture),
      ...Object.fromEntries(['mainOdds', 'mainOddsStatus', 'marketState', 'marketsPreview', 'markets', 'marketSnapshot', 'odds',
        'score', 'scoreHome', 'scoreAway', 'score_home', 'score_away', 'live_score_home', 'live_score_away',
        'clock', 'period', 'periodNumber']
        .filter(field => fixture[field] !== undefined).map(field => [field, fixture[field]])),
    }));
    next = applyListPatch(next, { fixtures });
  }
  return next;
};
